import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage, useLanguageFromUrl } from "@/context/LanguageContext";

// Pages
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import WeddingPage from "@/pages/WeddingPage";
import BookingPage from "@/pages/BookingPage";
import GalleryPage from "@/pages/GalleryPage";
import ContactPage from "@/pages/ContactPage";

// Components
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Component for hreflang tags
function SEOTags() {
  const location = useLocation();
  const { language } = useLanguage();
  
  useEffect(() => {
    // Update document language
    document.documentElement.lang = language;
    
    // Get current path without language prefix
    const pathParts = location.pathname.split("/");
    const pathWithoutLang = pathParts.slice(2).join("/") || "";
    
    // Create hreflang links
    const existingLinks = document.querySelectorAll('link[hreflang]');
    existingLinks.forEach(link => link.remove());
    
    const noLink = document.createElement('link');
    noLink.rel = 'alternate';
    noLink.hreflang = 'no';
    noLink.href = `${window.location.origin}/no/${pathWithoutLang}`;
    document.head.appendChild(noLink);
    
    const enLink = document.createElement('link');
    enLink.rel = 'alternate';
    enLink.hreflang = 'en';
    enLink.href = `${window.location.origin}/en/${pathWithoutLang}`;
    document.head.appendChild(enLink);
    
    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    defaultLink.href = `${window.location.origin}/no/${pathWithoutLang}`;
    document.head.appendChild(defaultLink);
    
    return () => {
      document.querySelectorAll('link[hreflang]').forEach(link => link.remove());
    };
  }, [location.pathname, language]);
  
  return null;
}

// Layout wrapper that syncs language from URL
function AppLayout({ children }) {
  useLanguageFromUrl();
  
  return (
    <>
      <SEOTags />
      <Navbar />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </>
  );
}

// Localized routes wrapper
function LocalizedRoutes() {
  return (
    <Routes>
      {/* Default redirect to Norwegian */}
      <Route path="/" element={<Navigate to="/no" replace />} />
      
      {/* Norwegian routes */}
      <Route path="/no" element={<AppLayout><HomePage /></AppLayout>} />
      <Route path="/no/services" element={<AppLayout><ServicesPage /></AppLayout>} />
      <Route path="/no/wedding" element={<AppLayout><WeddingPage /></AppLayout>} />
      <Route path="/no/booking" element={<AppLayout><BookingPage /></AppLayout>} />
      <Route path="/no/gallery" element={<AppLayout><GalleryPage /></AppLayout>} />
      <Route path="/no/contact" element={<AppLayout><ContactPage /></AppLayout>} />
      
      {/* English routes */}
      <Route path="/en" element={<AppLayout><HomePage /></AppLayout>} />
      <Route path="/en/services" element={<AppLayout><ServicesPage /></AppLayout>} />
      <Route path="/en/wedding" element={<AppLayout><WeddingPage /></AppLayout>} />
      <Route path="/en/booking" element={<AppLayout><BookingPage /></AppLayout>} />
      <Route path="/en/gallery" element={<AppLayout><GalleryPage /></AppLayout>} />
      <Route path="/en/contact" element={<AppLayout><ContactPage /></AppLayout>} />
      
      {/* Fallback - redirect old routes to Norwegian versions */}
      <Route path="/services" element={<Navigate to="/no/services" replace />} />
      <Route path="/wedding" element={<Navigate to="/no/wedding" replace />} />
      <Route path="/booking" element={<Navigate to="/no/booking" replace />} />
      <Route path="/gallery" element={<Navigate to="/no/gallery" replace />} />
      <Route path="/contact" element={<Navigate to="/no/contact" replace />} />
      
      {/* Catch all - redirect to Norwegian home */}
      <Route path="*" element={<Navigate to="/no" replace />} />
    </Routes>
  );
}

function App() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        if (response.data.length === 0) {
          await axios.post(`${API}/seed`);
          console.log("Data seeded successfully");
        }
      } catch (e) {
        console.error("Error checking/seeding data:", e);
      }
    };
    seedData();
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <div className="min-h-screen flex flex-col">
          {/* Grain texture overlay */}
          <div className="grain-overlay" aria-hidden="true" />
          <LocalizedRoutes />
          <Toaster position="top-right" richColors />
        </div>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
